/**
 * BigCommerceAIEditor
 *
 * Workflow node editor for the "BigCommerce AI" action.
 *
 * Architecture:
 *   ① AI Config         — which LLM + system prompt
 *   ② BigCommerce Config — which store (credentials)
 *   ③ MCP Servers       — tools the AI can call
 *   ④ Scenario Prompts  — copy/paste snippets for the AI Config system prompt
 *   ⑤ Specialists       — optional delegation
 *
 * BigCommerce live tools (admin controller):
 *   - bigcommerce_search_customer → GET /BigCommerce/Customers/Search (by email)
 *   - bigcommerce_search_orders   → GET /BigCommerce/Orders/Search (by order ID or customer email)
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { WorkflowNode } from "../../types";
import useAIConfigs from "@/hooks/useAIConfigs";
import useBigCommerceConfigs from "@/hooks/useBigCommerceConfigs";
import useMcpServers from "@/hooks/useMcpServers";
import AISetupGuide from "./AISetupGuide";
import AISpecialistSection from "./AISpecialistSection";
import AIJumpTargetSection from "./AIJumpTargetSection";
import AIEscalationSection from "./AIEscalationSection";
import AIContextInspector from "./AIContextInspector";
import McpStaleBadge from "./McpStaleBadge";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

const MCP_SUPPORTED_PROVIDERS = ["claude", "openai", "azure-openai", "anthropic"];

const BIGCOMMERCE_SCENARIOS = [
  {
    id: "order_status",
    label: "Order Status Lookup",
    description: "Customer wants to check the status of their order.",
    prompt: `When a customer asks about an order, ask for their order number or the email address on their account if not already provided. Use bigcommerce_search_orders to find the order. Return the order status, items ordered, total, and estimated delivery date. If the order is not found by order number, search by email to show all recent orders. If the order is awaiting shipment, give an honest timeline. If it is delayed or there is an issue, acknowledge it and offer to connect them with support.`
  },
  {
    id: "account_lookup",
    label: "Customer Account Lookup",
    description: "Look up the customer's account, order history, and account status.",
    prompt: `When the customer asks about their account or order history, use bigcommerce_search_customer with their email to find their record. Share their account status and a summary of recent orders (last 3-5). Do not share sensitive payment information. If the customer mentions a specific order issue, use bigcommerce_search_orders to get full order details. If the account is not found, offer to help them create one or proceed as a guest lookup.`
  },
  {
    id: "returns_refund",
    label: "Returns & Refund Inquiry",
    description: "Customer wants to return an item or request a refund.",
    prompt: `When a customer asks about a return or refund, first look up their order using bigcommerce_search_orders by email or order number. Confirm the items they want to return and check whether the order is within your return policy window (typically 30 days from delivery). Clearly explain the return process: [customize with your actual return process here]. Collect the reason for return. If eligible, provide a return authorization or direct them to the returns page. If outside the return window or ineligible, explain why and offer alternatives (exchange, store credit, etc.).`
  },
  {
    id: "product_availability",
    label: "Product & Order Guidance",
    description: "Help customers find products or understand their purchase options.",
    prompt: `When a customer asks about a product or wants help choosing what to order, use bigcommerce_search_customer to check if they are an existing customer with purchase history. Reference their past purchases to make relevant recommendations. If they have a pending order, offer to add items. For general product questions, answer based on your product knowledge. If you need to check live inventory or pricing, use the appropriate product search tool. Always confirm the customer's shipping address before placing any new order.`
  },
  {
    id: "post_purchase",
    label: "Post-Purchase Support",
    description: "Customer received their order but has an issue (damaged, wrong item, missing).",
    prompt: `When a customer reports a post-purchase issue (damaged item, wrong item, missing item), first look up the order using bigcommerce_search_orders with their email or order number. Confirm the order details match their description. Express empathy for the inconvenience. For damaged or wrong items: apologize, confirm you'll send a replacement at no charge, and ask for a photo of the issue if your process requires it. For missing items: check if the item was in the same shipment or a separate one. Escalate to a human agent for high-value orders or complex situations.`
  }
];

export default function BigCommerceAIEditor({ node, onUpdate }: Props) {
  const { aiConfigurations } = useAIConfigs();
  const { items: bigCommerceConfigs, loading: bcLoading } = useBigCommerceConfigs();
  const { items: mcpServers } = useMcpServers();
  const [copiedScenario, setCopiedScenario] = useState<string | null>(null);
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);

  const aiOptions = aiConfigurations.map((ai) => ({ value: ai.id, label: ai.name }));
  const bcOptions = bigCommerceConfigs
    .filter((b) => b.enabled)
    .map((b) => ({
      value: b.id,
      label: b.displayName || b.storeName || `Config #${b.id}`
    }));
  const allBcOptions = bigCommerceConfigs.map((b) => ({
    value: b.id,
    label: (b.displayName || b.storeName || `Config #${b.id}`) + (b.enabled ? "" : " (disabled)")
  }));

  const selectedAiConfig = aiConfigurations.find((ai) => ai.id === node.aiConfigId);
  const enabledMcpServers = mcpServers.filter((s) => s.enabled);

  const providerName =
    (selectedAiConfig as { provider?: string } | undefined)?.provider?.toLowerCase() ?? "";
  const providerUnsupported =
    enabledMcpServers.length > 0 &&
    selectedAiConfig != null &&
    providerName !== "" &&
    !MCP_SUPPORTED_PROVIDERS.some((p) => providerName.includes(p));

  const selectedMcpIds: number[] = Array.isArray(node.mcpServerIds) ? node.mcpServerIds : [];
  const enabledToolNames = enabledMcpServers
    .filter((s) => selectedMcpIds.includes(s.id))
    .flatMap((s) => s.toolNames ?? []);

  function toggleMcpServer(id: number, checked: boolean) {
    const next = checked ? [...selectedMcpIds, id] : selectedMcpIds.filter((x) => x !== id);
    onUpdate({ mcpServerIds: next.length > 0 ? next : undefined });
  }

  async function copyScenarioPrompt(id: string, prompt: string) {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedScenario(id);
      setTimeout(() => setCopiedScenario(null), 2000);
    } catch { /* graceful fallback */ }
  }

  return (
    <>
      <AISetupGuide
        dataSourceName="BigCommerce"
        dataSourcePath="/integrations/bigcommerce"
        hasAiConfig={!!selectedAiConfig}
        hasMcpServers={enabledMcpServers.length > 0}
        hasDataSourceConfig={!!node.bigCommerceConfigId}
      />

      {/* ── Step 1: AI Config ── */}
      <FormGroup label="AI Config">
        {aiConfigurations.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No AI configs found. <Link to="/bots" className="underline font-medium">Create one first →</Link>
          </div>
        ) : (
          <Select
            options={[{ value: 0, label: "— Select AI Config —" }, ...aiOptions]}
            value={node.aiConfigId ?? 0}
            onChange={(value) => onUpdate({ aiConfigId: Number(value) || undefined })}
          />
        )}
        {selectedAiConfig && !selectedAiConfig.prompt && (
          <p className="text-xs text-amber-600 mt-1">
            No system prompt yet. <Link to="/bots" className="underline">Add one →</Link> Use the Scenario Prompts below.
          </p>
        )}
      </FormGroup>

      {/* ── Step 2: BigCommerce Config ── */}
      <FormGroup label="BigCommerce Store">
        {!bcLoading && bigCommerceConfigs.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No BigCommerce store connected.{" "}
            <Link to="/integrations/bigcommerce" className="underline font-medium">Connect a store →</Link>
            <p className="mt-1 text-slate-500">
              The BigCommerce config stores your store hash and API token so the AI can search
              customers and orders.
            </p>
          </div>
        ) : (
          <>
            <Select
              options={[
                { value: 0, label: "— Select BigCommerce Store —" },
                ...(bcOptions.length > 0 ? bcOptions : allBcOptions)
              ]}
              value={node.bigCommerceConfigId ?? 0}
              onChange={(value) => onUpdate({ bigCommerceConfigId: Number(value) || undefined })}
            />
            {bigCommerceConfigs.length > 0 && bcOptions.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                All stores are disabled. <Link to="/integrations/bigcommerce" className="underline">Enable one →</Link>
              </p>
            )}
          </>
        )}
      </FormGroup>

      {/* ── Step 3: MCP Tool Servers ── */}
      <FormGroup label="MCP Tool Servers">
        {enabledMcpServers.length === 0 ? (
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
            No MCP tool servers configured.{" "}
            <Link to="/integrations/mcp" className="text-blue-600 underline">Add an MCP server →</Link>
            <p className="mt-1">
              MCP servers give the AI tools:{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">bigcommerce_search_orders</code>,{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">bigcommerce_search_customer</code>,{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">bigcommerce_get_product</code>, etc.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 mt-1">
            {providerUnsupported && (
              <p className="text-amber-700 bg-amber-50 text-xs px-2 py-1 rounded border border-amber-200">
                MCP tools require Claude or OpenAI. The selected AI config may not support them.
              </p>
            )}
            {enabledMcpServers.map((server) => {
              const checked = selectedMcpIds.includes(server.id);
              return (
                <label
                  key={server.id}
                  className={`flex gap-2.5 cursor-pointer rounded border p-2.5 transition-colors ${
                    checked ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 shrink-0"
                    checked={checked}
                    onChange={(e) => toggleMcpServer(server.id, e.target.checked)}
                    aria-label={`Enable MCP server: ${server.name}`}
                  />
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{server.name}</span>
                      {server.toolCount > 0 && (
                        <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                          {server.toolCount} {server.toolCount === 1 ? "tool" : "tools"}
                        </span>
                      )}
                      <McpStaleBadge lastDiscoveredAt={server.lastDiscoveredAt} />
                    </div>
                    {server.description && <span className="text-xs text-slate-500">{server.description}</span>}
                    {server.toolNames.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {server.toolNames.slice(0, 6).map((t) => (
                          <span key={t} className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-mono">{t}</span>
                        ))}
                        {server.toolNames.length > 6 && (
                          <span className="text-xs text-slate-400 px-1 py-0.5">+{server.toolNames.length - 6} more</span>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </FormGroup>

      {/* ── Scenario Prompts ── */}
      <div className="mt-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Scenario Prompts</span>
          <span className="text-xs text-slate-400 italic">for your AI Config</span>
        </div>
        <p className="text-xs text-slate-500 mb-2 leading-relaxed">
          Copy these into your AI Config's system prompt. Customize the bracketed sections for your store's specific policies.
        </p>
        <div className="flex flex-col gap-2">
          {BIGCOMMERCE_SCENARIOS.map((scenario) => {
            const isExpanded = expandedScenario === scenario.id;
            const isCopied = copiedScenario === scenario.id;
            return (
              <div key={scenario.id} className="border border-slate-200 rounded bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedScenario(isExpanded ? null : scenario.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                  aria-expanded={isExpanded}
                >
                  <div>
                    <span className="text-xs font-medium text-slate-800">{scenario.label}</span>
                    {!isExpanded && <p className="text-xs text-slate-400 mt-0.5">{scenario.description}</p>}
                  </div>
                  <span className="text-slate-400 text-xs ml-2 shrink-0">{isExpanded ? "▲" : "▼"}</span>
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3">
                    <p className="text-xs text-slate-500 mb-1.5">{scenario.description}</p>
                    <pre className="text-xs bg-slate-900 text-slate-100 rounded p-2.5 whitespace-pre-wrap leading-relaxed font-mono mb-2">
                      {scenario.prompt}
                    </pre>
                    <button
                      type="button"
                      onClick={() => copyScenarioPrompt(scenario.id, scenario.prompt)}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                        isCopied ? "border-green-300 bg-green-50 text-green-700" : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      }`}
                    >
                      {isCopied ? "✓ Copied!" : "Copy prompt snippet"}
                    </button>
                    <p className="text-xs text-slate-400 mt-1.5">Paste into your AI Config's system prompt, then customize the bracketed sections.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <AIContextInspector mcpToolNames={enabledToolNames} />

      <div className="mt-4 pt-3 border-t border-slate-200">
        <AISpecialistSection node={node} onUpdate={onUpdate} aiConfigurations={aiConfigurations} enabledMcpServers={enabledMcpServers} />
      </div>
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIEscalationSection node={node} onUpdate={onUpdate} />
      </div>
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIJumpTargetSection node={node} onUpdate={onUpdate} />
      </div>
    </>
  );
}
